import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface Model {
  id: string;
  display_name: string;
  description: string | null;
  elo_rating: number;
  wins: number;
  losses: number;
  ties: number;
  win_rate: number;
  is_active: boolean;
}

export default function ModelManager() {
  const { authToken } = useAuth();
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const headers: Record<string, string> = {
    Authorization: `Bearer ${authToken}`,
    'Content-Type': 'application/json',
  };

  const fetchModels = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/admin/models`, { headers });
      if (!res.ok) throw new Error(`Failed to fetch models: ${res.status}`);
      const data = await res.json();
      setModels(data.models || data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load models');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const toggleActive = async (model: Model) => {
    setTogglingId(model.id);
    try {
      const res = await fetch(`${API_BASE}/admin/models/${model.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ is_active: !model.is_active }),
      });
      if (!res.ok) throw new Error(`Failed to update model: ${res.status}`);
      setModels((prev) =>
        prev.map((m) =>
          m.id === model.id ? { ...m, is_active: !m.is_active } : m
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle model');
    } finally {
      setTogglingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-arena-accent border-t-transparent rounded-full animate-spin" />
        <span className="ml-3 text-arena-muted">Loading models...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
        {error}
        <button onClick={fetchModels} className="ml-3 underline hover:no-underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-arena-muted">
          {models.length} model{models.length !== 1 ? 's' : ''} registered
        </p>
        <button onClick={fetchModels} className="btn btn-secondary text-sm">
          Refresh
        </button>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-arena-border text-arena-muted text-sm">
                <th className="text-left p-3 font-medium">Name</th>
                <th className="text-right p-3 font-medium">ELO</th>
                <th className="text-right p-3 font-medium">W / L / T</th>
                <th className="text-right p-3 font-medium">Win Rate</th>
                <th className="text-center p-3 font-medium">Active</th>
              </tr>
            </thead>
            <tbody>
              {models.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-arena-muted">
                    No models found.
                  </td>
                </tr>
              ) : (
                models.map((model) => (
                  <tr
                    key={model.id}
                    className="border-b border-arena-border last:border-0 hover:bg-arena-surface/50 transition-colors"
                  >
                    <td className="p-3">
                      <div className="font-medium text-arena-text">{model.display_name}</div>
                      {model.description && (
                        <div className="text-xs text-arena-muted truncate max-w-xs">
                          {model.description}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <span className="font-mono text-lg font-bold">{model.elo_rating}</span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="font-mono text-sm">
                        <span className="text-green-400">{model.wins}</span>
                        <span className="text-arena-muted mx-1">/</span>
                        <span className="text-red-400">{model.losses}</span>
                        <span className="text-arena-muted mx-1">/</span>
                        <span className="text-arena-muted">{model.ties}</span>
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-2 bg-arena-border rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-400 rounded-full"
                            style={{ width: `${model.win_rate}%` }}
                          />
                        </div>
                        <span className="font-mono text-sm w-12 text-right">
                          {model.win_rate}%
                        </span>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <button
                        onClick={() => toggleActive(model)}
                        disabled={togglingId === model.id}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          model.is_active ? 'bg-arena-accent' : 'bg-arena-border'
                        } ${togglingId === model.id ? 'opacity-50' : ''}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                            model.is_active ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
